import { FC, useEffect } from "react";
import { X } from "lucide-react";
import cx from "classnames";
import { createPortal } from "react-dom";
import { useCart } from "./CartContext";
import useMediaQuery from "@/custom_hooks/mediaQuery";
import ROUTES from "@/data/Routes";
import { pushEvent } from "@/utils/gtm";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/UI/Buttons/Button";
import { Icons } from "@/assets";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer: FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const { cartItems, updateQuantity, removeFromCart, totals, discount } =
    useCart();
  const isMobile = useMediaQuery(768);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && isMobile) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (isOpen) {
      pushEvent("view_cart");
    }
  }, [isOpen]);

  const increaseQuantity = async (id: number) => {
    const item = cartItems.find((item) => item.id === id);
    if (!item) return;

    try {
      await updateQuantity(id, item.quantity + 1);
    } catch (error) {
      console.error("Error increasing quantity:", error);
    }
  };

  const decreaseQuantity = async (id: number) => {
    const item = cartItems.find((item) => item.id === id);
    if (!item || item.quantity <= 1) return;

    try {
      await updateQuantity(id, item.quantity - 1);
    } catch (error) {
      console.error("Error decreasing quantity:", error);
    }
  };

  const computedTotal = cartItems
    .reduce((sum, item) => sum + (item.price_cents * item.quantity) / 100, 0)
    .toFixed(2);
  const total = totals?.total ?? `€${computedTotal}`;
  const preTotal = totals?.preTotal;

  if (typeof window === "undefined") return null;

  return createPortal(
    <>
      {isOpen && (
        <div className="fixed inset-0 z-51 bg-black/40" onClick={onClose} />
      )}

      <div
        className={cx(
          "bg-paper border-ink/15 fixed top-0 right-0 z-52 flex h-[100dvh] w-full max-w-[541px] flex-col border-l shadow-lg transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
        style={{
          paddingTop: "max(1rem, env(safe-area-inset-top))",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={cx(
            "border-ink/10 flex items-center justify-between gap-4 border-b pb-4",
            isMobile ? "px-5 pt-2" : "px-[54px] pt-3",
          )}
        >
          <div className="flex flex-col">
            <p className="text-ink text-[18px] leading-[1.3] font-medium">
              Krepšelis
            </p>
            <p className="text-ink/60 text-sm">
              {cartItems.length ? `${cartItems.length} prekės` : "Tuščias"}
            </p>
          </div>

          <button
            onClick={onClose}
            aria-label="Close cart drawer"
            className="hover:bg-ink/5 grid size-10 place-items-center rounded-full transition-colors"
          >
            <X className="text-ink/60 size-5" />
          </button>
        </div>

        {/* Cart Items */}
        <div
          className={cx(
            "flex-1 overflow-y-auto",
            isMobile ? "px-5 py-6" : "px-[54px] py-7",
          )}
        >
          {cartItems.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <p className="text-ink text-[18px] leading-[1.3] font-medium">
                Jūsų krepšelis yra tuščias
              </p>
              <p className="text-ink/60 text-sm">
                Pridėkite norimas paslaugas ir grįžkite čia apmokėjimui.
              </p>
              <Button
                variant="white"
                size="sm"
                type="button"
                onClick={onClose}
                className="border-ink text-ink hover:bg-ink/5 h-[56px] border bg-white px-7 shadow-none"
                onHover
                hoverDark
              >
                Tęsti naršymą
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="border-ink/10 flex flex-col gap-4 border-b pb-5"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="bg-paper border-ink grid size-11 place-items-center rounded-full border">
                        <img
                          src={Icons.Lighting}
                          alt=""
                          aria-hidden="true"
                          className="size-6 object-contain"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <p className="text-ink text-[16px] leading-[1.35] font-medium">
                          {item.name}
                        </p>
                        <p className="text-muted text-sm">
                          €{item.price_cents / 100}
                        </p>
                      </div>
                    </div>
                    <p className="text-ink text-[16px] leading-[1.35] font-medium">
                      €{(item.price_cents * item.quantity) / 100}
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="rounded-button border-ink/20 flex h-[44px] items-center gap-2 border px-2">
                      <button
                        type="button"
                        className="hover:bg-ink/5 grid size-9 place-items-center rounded-full transition-colors"
                        onClick={() => decreaseQuantity(item.id)}
                        aria-label="Decrease quantity"
                        disabled={item.quantity <= 1}
                      >
                        <span className="text-ink text-[18px] leading-none">
                          −
                        </span>
                      </button>
                      <span className="text-ink w-8 text-center text-[15px]">
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        className="hover:bg-ink/5 grid size-9 place-items-center rounded-full transition-colors"
                        onClick={() => increaseQuantity(item.id)}
                        aria-label="Increase quantity"
                      >
                        <span className="text-ink text-[18px] leading-none">
                          +
                        </span>
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeFromCart(item.id)}
                      className="text-ink/60 hover:text-ink text-sm underline underline-offset-4 transition-colors"
                    >
                      Pašalinti
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Total & Checkout Button */}
        {cartItems.length > 0 && (
          <div
            className={cx(
              "bg-paper border-ink/10 sticky bottom-0 border-t",
              isMobile ? "px-5 py-5" : "px-[54px] py-6",
            )}
            style={{
              paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))",
            }}
          >
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4">
                <p className="text-ink text-[16px] leading-[1.35] font-medium">
                  Tarpinė suma
                </p>
                <div className="flex items-baseline gap-3">
                  {preTotal && (
                    <span className="text-ink/60 text-sm line-through">
                      {preTotal}
                    </span>
                  )}
                  <span className="text-ink text-[16px] leading-[1.35] font-medium">
                    €{total.split(".")[0]}
                  </span>
                </div>
              </div>

              {discount?.code && (
                <p className="text-ink/60 text-sm">
                  Nuolaidos kodas:{" "}
                  <span className="text-ink">{discount.code}</span>
                </p>
              )}

              <div className="mt-2 flex flex-col gap-3">
                <Button
                  type="button"
                  variant="ink"
                  size="checkout"
                  onClick={() => {
                    pushEvent("initiate_checkout");
                    onClose();
                    navigate(ROUTES.CHECKOUT);
                  }}
                  onHover
                  hoverLight
                >
                  Pereiti į apmokėjimą
                </Button>
                <Button
                  type="button"
                  variant="white"
                  size="checkout"
                  onClick={onClose}
                  className="border-ink text-ink hover:bg-ink/5 border bg-white shadow-none"
                  onHover
                  hoverDark
                >
                  Tęsti apsipirkimą
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};

export default CartDrawer;
